import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { Clipboard } from '@capacitor/clipboard';
import {
  IonButton,
  IonButtons,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonModal,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonToggle,
  IonToolbar
} from '@ionic/angular/standalone';
import { TranslateModule } from '@ngx-translate/core';
import { addIcons } from 'ionicons';
import { checkmarkOutline, clipboardOutline, closeOutline, compassOutline } from 'ionicons/icons';
import { firstValueFrom } from 'rxjs';

import { SurfaceType } from '@core/models/notification';
import { OnboardingFlow, OnboardingStep } from '@core/models/onboarding';
import { EatingOccasion } from '@core/models/order';
import { stepsForFlow } from '@core/onboarding/onboarding-machine';
import { JsonHighlightPipe } from '@core/pipes/json-highlight';
import { AccountService } from '@core/services/account/account';
import { AddressService } from '@core/services/address/address';
import { AdminService } from '@core/services/admin/admin';
import { AuthService } from '@core/services/auth/auth';
import { BiometricService } from '@core/services/biometric/biometric';
import { CacheService } from '@core/services/cache/cache';
import { DeepLinkService } from '@core/services/deep-link/deep-link';
import { DeviceService } from '@core/services/device/device';
import { GroupService } from '@core/services/group/group';
import { HealthService } from '@core/services/health/health';
import { HistoryService, HistoryType } from '@core/services/history/history';
import { LocationService } from '@core/services/location/location';
import { NetworkService } from '@core/services/network/network';
import { NotificationService } from '@core/services/notification/notification';
import { NutrientProgramService } from '@core/services/nutrient-program/nutrient-program';
import { OnboardingStateService } from '@core/services/onboarding/onboarding-state';
import { OrderService } from '@core/services/order/order';
import { PaymentService } from '@core/services/payment/payment';
import { PlatformService } from '@core/services/platform/platform';
import { UserService } from '@core/services/user/user';

enum ItemType {
  Toggle = 'toggle',
  Button = 'button'
}

@Component({
  selector: 'yt-admin-overlay',
  templateUrl: 'admin-overlay.html',
  styleUrls: ['admin-overlay.scss'],
  imports: [
    IonFooter,
    IonIcon,
    IonModal,
    IonButtons,
    IonTitle,
    IonToolbar,
    IonHeader,
    IonButton,
    IonListHeader,
    IonList,
    IonItem,
    IonSegment,
    IonSegmentButton,
    IonLabel,
    TranslateModule,
    IonToggle,
    JsonHighlightPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminOverlay {
  private readonly adminService = inject(AdminService);
  private readonly onboardingService = inject(OnboardingStateService);
  private readonly authService = inject(AuthService);
  private readonly accountService = inject(AccountService);
  private readonly addressService = inject(AddressService);
  private readonly biometricService = inject(BiometricService);
  private readonly deepLinkService = inject(DeepLinkService);
  private readonly deviceService = inject(DeviceService);
  private readonly groupService = inject(GroupService);
  private readonly healthService = inject(HealthService);
  private readonly locationService = inject(LocationService);
  private readonly networkService = inject(NetworkService);
  private readonly notificationService = inject(NotificationService);
  private readonly nutrientProgramService = inject(NutrientProgramService);
  private readonly cacheService = inject(CacheService);
  private readonly orderService = inject(OrderService);
  private readonly paymentService = inject(PaymentService);
  private readonly platformService = inject(PlatformService);
  private readonly historyService = inject(HistoryService);
  private readonly userService = inject(UserService);

  protected readonly show = this.adminService.isUnlocked;
  protected readonly isAuthenticated = this.authService.isAuthenticated;
  protected readonly onboardingState = this.onboardingService.state;
  protected readonly completedLearning = this.onboardingService.completedLearning;
  protected readonly authUser = this.authService.authUser;
  protected readonly accessToken = this.authService.accessToken;
  protected readonly refreshToken = this.authService.refreshToken;
  protected readonly tokenExpiry = this.authService.tokenExpiry;
  protected readonly externalUser = this.userService.externalUser;
  protected readonly user = this.userService.user;
  protected readonly userInvitations = this.userService.userInvitations;
  protected readonly availabilityAddress = this.accountService.availabilityAddress;
  protected readonly availabilityResult = this.accountService.availabilityResult;
  protected readonly account = computed(() => this.accountService.accountMap()[this.viewingUser()?.userId ?? '']);
  protected readonly accountMemberships = computed(() => {
    const account = this.account();
    return account ? this.accountService.accountMembershipsMap()[account.accountId] : undefined;
  });
  protected readonly completedOnboarding = this.accountService.completedOnboarding;
  protected readonly completedOnboardingLearning = this.accountService.completedOnboardingLearning;
  protected readonly addresses = computed(
    () => this.addressService.addressesMap()[this.viewingUser()?.userId ?? ''] ?? []
  );
  protected readonly biometricsAvailable = this.biometricService.available;
  protected readonly biometricsEnabled = this.biometricService.enabled;
  protected readonly deepLink = this.deepLinkService.deepLink;
  protected readonly deviceInfo = this.deviceService.deviceInfo;
  protected readonly group = this.groupService.group;
  protected readonly groupInvitations = this.groupService.groupInvitations;
  protected readonly viewingUser = this.groupService.viewingUser;
  protected readonly viewingUserIsMe = this.groupService.viewingUserIsMe;
  protected readonly viewingHousehold = this.groupService.viewingHousehold;
  protected readonly healthProfile = computed(
    () => this.healthService.healthProfileMap()[this.viewingUser()?.userId ?? '']
  );
  protected readonly location = this.locationService.location;
  protected readonly locationAddress = this.locationService.locationAddress;
  protected readonly networkConnected = this.networkService.connected;
  protected readonly networkConnectionType = this.networkService.connectionType;
  protected readonly notificationsAvailable = this.notificationService.available;
  protected readonly notificationsEnabled = this.notificationService.enabled;
  protected readonly fcmToken = this.notificationService.fcmToken;
  protected readonly notifications = this.notificationService.notifications;
  protected readonly foregroundNotification = this.notificationService.foregroundNotification;
  protected readonly nutrientProgram = this.nutrientProgramService.householdProgram;
  protected readonly nutrientProgramDevLock = this.nutrientProgramService.devEditLock;
  protected readonly cacheEntryCount = this.cacheService.entryCount;
  protected readonly cacheDisabled = this.cacheService.disabled;
  protected readonly orderContext = this.orderService.orderContext;
  protected readonly order = this.orderService.activeOrder;
  protected readonly orders = this.orderService.orders;
  protected readonly ordersTrackingMap = this.orderService.ordersTrackingMap;
  protected readonly deliveryWindows = this.orderService.deliveryWindows;
  protected readonly wallet = computed(() => {
    const account = this.account();
    return account?.walletId ? this.paymentService.walletMap()[account.walletId] : undefined;
  });
  protected readonly platform = this.platformService.platform;
  protected readonly isWeb = this.platformService.isWeb;
  protected readonly isAndroid = this.platformService.isAndroid;
  protected readonly isIos = this.platformService.isIos;
  protected readonly isMobile = this.platformService.isMobile;
  protected readonly foreground = this.platformService.foreground;
  protected readonly history = this.historyService.history;

  protected readonly tabs = [
    { value: 'general', label: 'Admin.General' },
    { value: 'onboarding', label: 'Admin.Onboarding' },
    { value: 'health', label: 'Admin.Health' },
    { value: 'ordering', label: 'Admin.Ordering' }
  ] as const;

  protected readonly activeTab = signal<string>('general');

  protected readonly activeSection = computed(() => {
    const tabIndex = this.tabs.findIndex((t) => t.value === this.activeTab());
    return this.sections()[tabIndex] ?? null;
  });

  protected readonly onboardingSteps = computed(() => {
    const state = this.onboardingState();
    return stepsForFlow(OnboardingFlow.Standard).map((def, index) => ({
      step: def.step,
      label: `Admin.Step_${def.step}`,
      position: index,
      completed: state?.completedSteps?.includes(def.step) ?? false,
      current: state?.currentStep === def.step
    }));
  });

  protected readonly showAppState = signal<boolean>(false);
  protected readonly appState = computed(() => {
    return {
      onboarding: {
        state: this.onboardingState,
        completedLearning: this.completedLearning
      },
      auth: {
        isAuthenticated: this.isAuthenticated(),
        authUser: this.authUser(),
        accessToken: this.accessToken(),
        refreshToken: this.refreshToken(),
        tokenExpiry: this.tokenExpiry()
      },
      user: {
        externalUser: this.externalUser(),
        user: this.user(),
        userInvitations: this.userInvitations()
      },
      account: {
        availabilityAddress: this.availabilityAddress(),
        availabilityResult: this.availabilityResult(),
        account: this.account(),
        accountMemberships: this.accountMemberships(),
        completedOnboarding: this.completedOnboarding(),
        completedOnboardingLearning: this.completedOnboardingLearning()
      },
      address: {
        addresses: this.addresses()
      },
      biometrics: {
        available: this.biometricsAvailable(),
        enabled: this.biometricsEnabled()
      },
      deepLink: {
        deepLink: this.deepLink()
      },
      device: {
        deviceInfo: this.deviceInfo()
      },
      group: {
        group: this.group(),
        groupInvitations: this.groupInvitations(),
        viewingUser: this.viewingUser(),
        viewingUserIsMe: this.viewingUserIsMe(),
        viewingHousehold: this.viewingHousehold()
      },
      health: {
        healthProfile: this.healthProfile()
      },
      location: {
        location: this.location(),
        locationAddress: this.locationAddress()
      },
      network: {
        connected: this.networkConnected(),
        connectionType: this.networkConnectionType()
      },
      notifications: {
        available: this.notificationsAvailable(),
        enabled: this.notificationsEnabled(),
        fcmToken: this.fcmToken(),
        notifications: this.notifications(),
        foregroundNotification: this.foregroundNotification()
      },
      nutrientProgram: {
        nutrientProgram: this.nutrientProgram()
      },
      order: {
        orderContext: this.orderContext(),
        order: this.order(),
        orders: this.orders(),
        ordersTrackingMap: this.ordersTrackingMap(),
        deliveryWindows: this.deliveryWindows()
      },
      cache: {
        entryCount: this.cacheEntryCount(),
        entries: this.cacheService.getManifest()
      },
      payment: {
        wallet: this.wallet()
      },
      platform: {
        platform: this.platform(),
        isWeb: this.isWeb(),
        isMobile: this.isMobile(),
        isAndroid: this.isAndroid(),
        isIos: this.isIos()
      }
    };
  });
  protected readonly appStateString = computed(() => JSON.stringify(this.appState(), null, 2));
  protected readonly showHistory = signal<boolean>(false);
  protected readonly historyString = computed(() => JSON.stringify(this.history(), null, 2));

  protected readonly sections = computed(() => {
    return [
      {
        label: 'Admin.General',
        items: [
          {
            label: 'Admin.View_App_State',
            handler: this.toggleShowAppState.bind(this),
            value: this.showAppState(),
            type: ItemType.Button,
            disabled: false
          },
          {
            label: 'Admin.View_History',
            handler: this.toggleShowHistory.bind(this),
            value: this.showHistory(),
            type: ItemType.Button,
            disabled: false
          },
          {
            label: 'Admin.Go_To_Home',
            handler: this.goToHome.bind(this),
            value: true,
            type: ItemType.Button,
            disabled: false
          },
          {
            label: 'Admin.Go_To_Settings',
            handler: this.goToSettings.bind(this),
            value: true,
            type: ItemType.Button,
            disabled: false
          },
          {
            label: 'Admin.Biometrics_Available_Web_Only',
            value: this.biometricsAvailable(),
            handler: this.toggleBiometricsAvailable.bind(this),
            type: ItemType.Toggle,
            disabled: !this.isWeb()
          },
          {
            label: 'Admin.Disable_HTTP_Cache',
            value: this.cacheDisabled(),
            handler: this.toggleHttpCache.bind(this),
            type: ItemType.Toggle,
            disabled: false
          },
          {
            label: 'Admin.Clear_HTTP_Cache',
            handler: this.clearHttpCache.bind(this),
            value: true,
            type: ItemType.Button,
            disabled: false
          },
          {
            label: 'Admin.Show_Test_Notification',
            handler: this.fireTestNotification.bind(this),
            value: true,
            type: ItemType.Button,
            disabled: false
          }
        ]
      },
      {
        label: 'Admin.Onboarding',
        items: [
          {
            label: 'Admin.Clear_Onboarding_State',
            handler: this.clearOnboardingState.bind(this),
            value: true,
            type: ItemType.Button,
            disabled: false
          },
          {
            label: 'Admin.Clear_Availability_Address',
            handler: this.clearAvailabilityAddress.bind(this),
            value: true,
            type: ItemType.Button,
            disabled: false
          },
          {
            label: 'Admin.Available_In_Area',
            value: this.availabilityResult(),
            handler: this.toggleAvailabilityResult.bind(this),
            type: ItemType.Toggle,
            disabled: false
          },
          {
            label: 'Admin.Onboarding_Completed',
            value: this.completedOnboarding(),
            handler: this.toggleCompletedOnboarding.bind(this),
            type: ItemType.Toggle,
            disabled: false
          },
          {
            label: 'Admin.Onboarding_Learning_Completed',
            value: this.completedOnboardingLearning(),
            handler: this.toggleCompletedOnboardingLearning.bind(this),
            type: ItemType.Toggle,
            disabled: false
          }
        ]
      },
      {
        label: 'Admin.Health',
        items: [
          {
            label: 'Admin.Starting_Line_Complete',
            value: this.healthProfile()?.startingLineComplete,
            handler: this.toggleStartingLineComplete.bind(this),
            type: ItemType.Toggle,
            disabled: false
          },
          {
            label: 'Admin.N_Of_1_Enabled',
            value: this.healthProfile()?.nOf1Enabled,
            handler: this.toggleNOf1Enabled.bind(this),
            type: ItemType.Toggle,
            disabled: false
          }
        ]
      },
      {
        label: 'Admin.Ordering',
        items: [
          {
            label: 'Admin.Clear_Cart',
            handler: this.clearCart.bind(this),
            value: true,
            type: ItemType.Button,
            disabled: false
          },
          {
            label: 'Admin.Lock_Nutrient_Program',
            value: this.nutrientProgramDevLock(),
            handler: this.toggleNutrientProgramLock.bind(this),
            type: ItemType.Toggle,
            disabled: false
          },
          {
            label: 'Admin.Trigger_Sold_Out',
            handler: this.triggerSoldOut.bind(this),
            value: true,
            type: ItemType.Button,
            disabled: false
          }
        ]
      }
    ];
  });

  protected readonly ItemType = ItemType;
  protected readonly HistoryType = HistoryType;
  protected readonly OnboardingStep = OnboardingStep;

  constructor() {
    addIcons({ checkmarkOutline, clipboardOutline, closeOutline, compassOutline });
  }

  goToHome() {
    this.adminService.goTo(['home']);
  }

  goToSettings() {
    this.adminService.goTo(['settings']);
  }

  toggleBiometricsAvailable(available: boolean) {
    this.adminService.toggleBiometricsAvailable(available);
  }

  clearOnboardingState() {
    this.adminService.clearOnboardingState();
    this.close();
  }

  clearAvailabilityAddress() {
    this.adminService.clearAvailabilityAddress();
  }

  toggleAvailabilityResult(available: boolean) {
    this.adminService.toggleAvailabilityResult(available);
  }

  toggleCompletedOnboarding(completedOnboarding: boolean) {
    this.adminService.toggleCompletedOnboarding(completedOnboarding);
  }

  toggleCompletedOnboardingLearning(completedOnboardingLearning: boolean) {
    this.adminService.toggleCompletedOnboardingLearning(completedOnboardingLearning);
  }

  toggleStartingLineComplete(complete: boolean) {
    this.adminService.toggleStartingLineComplete(complete);
  }

  toggleNOf1Enabled(enabled: boolean) {
    this.adminService.toggleNOf1Enabled(enabled);
  }

  toggleShowAppState(show: boolean) {
    this.showAppState.set(show);
  }

  toggleShowHistory(show: boolean) {
    this.showHistory.set(show);
  }

  clearCart() {
    this.adminService.clearCart();
  }

  toggleNutrientProgramLock(locked: boolean) {
    this.nutrientProgramService.devToggleEditLock(locked);
  }

  // Simulates the not-yet-wired backend sold-out event: flashes the sold-out card on the first Schedule & Save
  // day's lunch slot for the viewed member, then closes the overlay so the flash is visible on the schedule.
  triggerSoldOut() {
    this.nutrientProgramService.flashFirstScheduleSoldOut(EatingOccasion.Lunch);
    this.close();
  }

  toggleHttpCache(disabled: boolean) {
    this.cacheService.setDisabled(disabled);
  }

  clearHttpCache() {
    this.cacheService.invalidateAll();
  }

  fireTestNotification() {
    void this.notificationService.addNotification({
      id: crypto.randomUUID(),
      notificationType: 'TestNotification',
      surfaceType: SurfaceType.Ephemeral,
      title: 'Test Notification',
      body: 'This is a test in-app notification.',
      subtitle: 'Test Establishment',
      isRead: false,
      createdAt: new Date().toISOString(),
      expiresAt: ''
    });
  }

  async fireNutrientOnboarding() {
    const groupId = this.group()?.groupId;
    if (!groupId) return;
    await firstValueFrom(this.nutrientProgramService.postOnboarding(groupId));
  }

  setActiveTab(event: CustomEvent) {
    this.activeTab.set(event.detail.value);
  }

  jumpToStep(step: OnboardingStep) {
    this.adminService.jumpToOnboardingStep(step);
    this.close();
  }

  close() {
    this.showAppState.set(false);
    this.adminService.toggleUnlock(false);
  }

  async copyToClipboard(text: string) {
    await Clipboard.write({
      string: text
    });
  }
}
